import type { ReactNode } from "react";

import { Panel } from "@/components/ui/panel";
import { cn } from "@/lib/utils";

/**
 * A stat tile — a number that is the whole chart.
 *
 * Three of these carry the top of the Metrics screen because "how many questions are
 * live" is one value: a bar chart of a single number is the most common way a chart
 * misses its point. Proportional figures, not tabular: `tabular-nums` gives every
 * digit the width of a zero, which reads loose at this size. Columns of numbers in
 * the tables below use tabular; standalone values like these do not.
 *
 * `flag` puts the tile in the attention colour. It is for a figure that is waiting on
 * someone, not for the largest one — a screen where every tile is flagged says
 * nothing.
 */
export function Stat({
  label,
  value,
  hint,
  flag = false,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  flag?: boolean;
}) {
  return (
    <Panel className={cn("px-4 py-3", flag && "border-bank-attention")}>
      <p className="text-bank-muted font-mono text-[10.5px] tracking-[0.07em] uppercase">{label}</p>
      <p className={cn("mt-1 text-2xl font-semibold tracking-tight", flag && "text-bank-attention-ink")}>
        {value}
      </p>
      {hint ? <p className="text-bank-muted mt-0.5 font-mono text-[11px]">{hint}</p> : null}
    </Panel>
  );
}
